import axios, {
	InternalAxiosRequestConfig,
	AxiosResponse,
	AxiosError,
} from 'axios'

import { env } from '@/core/environments'
import { router } from '@/core/router'
import { store } from '@/core/store'
import { RootState } from '@/core/store'
import { logout, loginSuccess } from '@/core/store/slices/auth'
import { isPartnerBuild } from '@/core/utils/buildType'

const IS_PARTNER_BUILD = isPartnerBuild()
const PARTNER_DOMAIN =
	import.meta.env.VITE_PARTNER_API_DOMAIN || 'api.musicbrand.io'
const IS_LOCAL_APP_ENV = import.meta.env.VITE_APP_ENV === 'localhost'

const normalizeUrl = (value: string) => {
	if (value.startsWith('http://') || value.startsWith('https://')) {
		return value
	}

	return `https://${value}`
}

// Select the appropriate API URL based on build type
const getApiBaseUrl = () => {
	// For Brandtrack builds, use the env.API_URL (api.brandtrack.fm or local.api.btdev.net)
	if (!IS_PARTNER_BUILD) {
		return env.API_URL
	}

	// Allow an explicit partner API override to win for local testing.
	if (import.meta.env.VITE_PARTNER_API_DOMAIN) {
		return normalizeUrl(PARTNER_DOMAIN)
	}

	// In local app environment, keep partner builds pointed to the local API.
	if (IS_LOCAL_APP_ENV) {
		return env.API_URL
	}

	// For partner builds outside localhost, use the configured partner API domain.
	return normalizeUrl(PARTNER_DOMAIN)
}

const getPartnerHost = (): string => {
	if (IS_PARTNER_BUILD) {
		const fixedURL =
			import.meta.env.VITE_PARTNER_FIXED_API_URL || env.FIXED_API_URL
		// For fixed URLs, we just return this
		if (fixedURL?.length > 0) {
			return normalizeUrl(fixedURL)
		}

		const currentDomain = window.location.hostname
		const parts = currentDomain.split('.')
		if (parts.length > 2) {
			parts[0] = 'api'
			return 'https://' + parts.join('.')
		}
		return `https://api.${currentDomain}`
	}

	return ''
}

// Create axios instance
const apiClient = axios.create({
	baseURL: getApiBaseUrl(),
	timeout: env.API_TIMEOUT,
	headers: {
		'Content-Type': 'application/json',
		'X-Partner-Host': getPartnerHost(),
	},
})

// Create axios instance for public endpoints (without auth)
export const publicApiClient = axios.create({
	baseURL: getApiBaseUrl(),
	timeout: env.API_TIMEOUT,
	headers: {
		'Content-Type': 'application/json',
		'X-Partner-Host': getPartnerHost(),
	},
})

// Request interceptor for publicApiClient
publicApiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const state = store.getState() as RootState
		const language = state.ui.language

		if (language && config.headers) {
			config.headers['Accept-Language'] = language
		}

		// Add custom domain header for partner builds if needed
		if (IS_PARTNER_BUILD && config.headers) {
			const customDomain = import.meta.env.VITE_PARTNER_CUSTOM_DOMAIN
			if (customDomain) {
				config.headers['X-Partner-Host'] = customDomain
			}
		}

		return config
	},
	(error: AxiosError) => {
		return Promise.reject(error)
	}
)

// Track if a refresh is in progress
let isRefreshing = false
// Queue of failed requests to retry after token refresh
let failedQueue: any[] = []

// Process the queue of failed requests
const processQueue = (error: any, token: string | null = null) => {
	failedQueue.forEach((promise) => {
		if (error) {
			promise.reject(error)
		} else {
			promise.resolve(token)
		}
	})

	failedQueue = []
}

// Request interceptor
apiClient.interceptors.request.use(
	(config: InternalAxiosRequestConfig) => {
		const state = store.getState() as RootState
		const token = state.auth.token
		const language = state.ui.language

		if (token && config.headers) {
			config.headers.Authorization = `Bearer ${token}`
		}

		if (language && config.headers) {
			config.headers['Accept-Language'] = language
		}

		// Add custom domain header for partner builds if needed
		if (IS_PARTNER_BUILD && config.headers) {
			const customDomain = import.meta.env.VITE_PARTNER_CUSTOM_DOMAIN
			if (customDomain) {
				config.headers['X-Partner-Host'] = customDomain
			}
		}

		return config
	},
	(error: AxiosError) => {
		return Promise.reject(error)
	}
)

// Response interceptor
apiClient.interceptors.response.use(
	(response: AxiosResponse) => {
		return response
	},
	async (error: AxiosError) => {
		const originalRequest = error.config as InternalAxiosRequestConfig & {
			_retry?: boolean
		}

		const messagesToIngore: string[] = [
			'These credentials do not match our records',
			'Wrong number of segments',
		]
		const message: string = (error.response?.data as any)?.message ?? ''
		const code: number = (error.response?.data as any)?.code ?? 0

		if (error.response?.status === 402 && code === 2) {
			// Redirect to subscription page using SPA navigation
			router.navigate('/subscribe')
			return Promise.reject(error)
		}

		// If it's a 401 error and not a refresh token request itself
		if (
			error.response?.status === 401 &&
			messagesToIngore.filter((m) => message.includes(m)).length === 0 &&
			!originalRequest._retry &&
			originalRequest.url !== '/auth/refresh'
		) {
			if (isRefreshing) {
				// If a refresh is already in progress, queue this request
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject })
				})
					.then((token) => {
						if (originalRequest.headers) {
							originalRequest.headers.Authorization = `Bearer ${token}`
						}
						return apiClient(originalRequest)
					})
					.catch((err) => {
						return Promise.reject(err)
					})
			}

			originalRequest._retry = true
			isRefreshing = true

			try {
				// Get current state
				const state = store.getState() as RootState
				const currentUser = state.auth.user

				// Attempt to refresh the token
				const response = await axios.post(
					`${getApiBaseUrl()}/auth/refresh`, // Use the same API URL selection logic
					{},
					{
						headers: {
							'Content-Type': 'application/json',
							Authorization: `Bearer ${state.auth.token}`,
						},
					}
				)

				if (response.data.token) {
					// Update Redux store
					store.dispatch(
						loginSuccess({
							token: response.data.token,
							user: currentUser!,
						})
					)

					// Process queued requests
					processQueue(null, response.data.token)

					// Update auth header and retry original request
					if (originalRequest.headers) {
						originalRequest.headers.Authorization = `Bearer ${response.data.token}`
					}

					isRefreshing = false
					return apiClient(originalRequest)
				}
			} catch (refreshError) {
				// Refresh failed, process queue with error
				processQueue(refreshError)
				isRefreshing = false
				// Logout user
				store.dispatch(logout())
				return Promise.reject(refreshError)
			}
		}

		// For other errors, just reject the promise
		return Promise.reject(error)
	}
)

export default apiClient
